import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Fluid } from '../models/fluid.model';
import { ThermalOpOrderModel } from '../models/op-order.model';
import { Perforation } from '../models/perforation.model';
import { BaseOperation } from '../models/thermal-operation.model';
import { PressureResults, TemperatureResults } from '../models/thermal-results.model';
import { PericalOptions } from '../models/pericalOptions';
import { GasLiftValve } from '../models/gasLiftValve.model';

const PeriforApiEndpoint = `${environment.PeriforEndpoint}/api`;

@Injectable({
  providedIn: 'root'
})
export class ThermalOperationsService {
    private thermalOperations$: Observable<Partial<BaseOperation>[]> | null = null;
    private perforationsCache$: Observable<Perforation[]> | null = null;
    private thermalOperationCache: Record<string, Observable<BaseOperation>> = {};
    private previousOperationsCache: Record<string, Observable<Partial<BaseOperation>[]>> = {};
    private temperatureResultsCache: Record<string, Observable<TemperatureResults[]>> = {};
    private fluidsCache$: Observable<Fluid[]> | null = null;
    private annularResultsCache$: Observable<any[]> | null = null;
    private thermalPressureResultsCache$: Record<string, Observable<PressureResults[]>> = {};
    private gasLiftCache$: Observable<GasLiftValve[]> | null = null;
  
  constructor(private http: HttpClient) { }

  getThermalOperations(): Observable<Partial<BaseOperation>[]> {
    if (!this.thermalOperations$) {
      this.thermalOperations$ = this.http.get<Partial<BaseOperation>[]>(`${PeriforApiEndpoint}/thermalOperations/`)
        .pipe(
          catchError(() => {
            return of([]);
          }),
          shareReplay(1)
        );
    }
    setTimeout(() => this.thermalOperations$ = null, 1000);
    return this.thermalOperations$;
  }

  getPreviousThermalOperations(currentOperationId: string): Observable<Partial<BaseOperation>[]> {
    if (!this.previousOperationsCache[currentOperationId]) {
      this.previousOperationsCache[currentOperationId] = this.http
        .get<BaseOperation[]>(`${PeriforApiEndpoint}/thermalOperations/previous-ops?currentOperationId=${currentOperationId}`)
        .pipe(
          catchError(() => {
            return of([]);
          }),
          shareReplay(1)
        );
    }
    setTimeout(() => this.previousOperationsCache[currentOperationId] = null, 1000);
    return this.previousOperationsCache[currentOperationId];
  }

  getApbFinalConditionOperations(): Observable<Partial<BaseOperation>[]> {
    return this.http.get<BaseOperation[]>(`${PeriforApiEndpoint}/apb/thermalOperations`);
  }

  getThermalOperationById(operationId: string): Observable<BaseOperation> {
    if (!this.thermalOperationCache[operationId]) {
      this.thermalOperationCache[operationId] = this.http.get<BaseOperation>(`${PeriforApiEndpoint}/thermalOperations/${operationId}`)
        .pipe(
          catchError(() => {
            return of(null);
          }),
          shareReplay(1)
        );
    }
    setTimeout(() => this.thermalOperationCache[operationId] = null, 1000);
    return this.thermalOperationCache[operationId];
  }

  updateThermalOperation(operation: BaseOperation): Observable<boolean> {
    return this.http.put<boolean>(`${PeriforApiEndpoint}/thermalOperations/${operation.id}`, operation);
  }

  createThermalOperation(operation: BaseOperation): Observable<string> {
    return this.http.post<string>(`${PeriforApiEndpoint}/thermalOperations/`, operation);
  }

  deleteThermalOperation(operationId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${PeriforApiEndpoint}/thermalOperations/${operationId}`);
  }

  cloneThermalOperation(operationId: string, operationName: string): Observable<Partial<BaseOperation>> {
    return this.http.post<Partial<BaseOperation>>(`${PeriforApiEndpoint}/thermalOperations/${operationId}/clone`, {name: operationName});
  }

  updateThermalOperationOrder(order: ThermalOpOrderModel) {
    return this.http.put<boolean>(`${PeriforApiEndpoint}/thermalOperations/order`, order);
  }

  getThermalTemperatureResults(takeFirstThreeCols = false, getWholeDesignResults = false): Observable<TemperatureResults[]> {
    const cacheKey = `${takeFirstThreeCols}_${getWholeDesignResults}`; // Use the parameter values as the cache key

    if (!this.temperatureResultsCache[cacheKey]) {
      this.temperatureResultsCache[cacheKey] = this.http
        .get<TemperatureResults[]>(`${PeriforApiEndpoint}/thermalResults/temperatures?takeFirstThreeCols=${takeFirstThreeCols}&getWholeDesignResults=${getWholeDesignResults}`)
        .pipe(
          catchError(() => {
            return of([]);
          }),
          shareReplay(1)
        );
    }
    setTimeout(() => this.temperatureResultsCache[cacheKey] = null, 1000);
    return this.temperatureResultsCache[cacheKey];
  }

  public getDetailedAnnularResults(): Observable<any[]> {
    if (!this.annularResultsCache$) {
      this.annularResultsCache$ = this.http
        .get<TemperatureResults[]>(`${PeriforApiEndpoint}/thermalResults/annuli`)
        .pipe(
          catchError(() => of([])),
          shareReplay(1)
        );
    }
    setTimeout(() => this.annularResultsCache$ = null, 1000);
    return this.annularResultsCache$;
  }

  public getThermalPressureResults(takeFirstThreeCols = false, getWholeDesignResults = false): Observable<PressureResults[]> {
    const cacheKey = `${takeFirstThreeCols}_${getWholeDesignResults}`;
    if (!this.thermalPressureResultsCache$[cacheKey]) {
      this.thermalPressureResultsCache$[cacheKey] = this.http
        .get<PressureResults[]>(`${PeriforApiEndpoint}/thermalResults/pressures?takeFirstThreeCols=${takeFirstThreeCols}&getWholeDesignResults=${getWholeDesignResults}`)
        .pipe(
          catchError(() => of([])),
          shareReplay(1)
        );
    }
    setTimeout(() => this.thermalPressureResultsCache$[cacheKey] = null, 1000);
    return this.thermalPressureResultsCache$[cacheKey];
  }

  public getFluids(): Observable<Fluid[]> {
    if (!this.fluidsCache$) {
      this.fluidsCache$ = this.http.get<Fluid[]>(`${PeriforApiEndpoint}/thermalOperations/fluids`)
        .pipe(
          catchError(() => {
            return of([]);
          }),
          shareReplay(1)
        );
    }
    setTimeout(() => this.fluidsCache$ = null, 1000);
    return this.fluidsCache$;
  }

  getPerforations(): Observable<Perforation[]> {
    if (!this.perforationsCache$) {
      this.perforationsCache$ = this.http.get<Perforation[]>(`${PeriforApiEndpoint}/thermalOperations/perforations`)
        .pipe(
          catchError(() => {
            return of([]);
          }),
          shareReplay(1)
        );
    }
    setTimeout(() => this.perforationsCache$ = null, 1000);
    return this.perforationsCache$;
  }

  setPerforations(perforation: Perforation): Observable<Perforation> {
    return this.http.post<Perforation>(`${PeriforApiEndpoint}/thermalOperations/perforations`, perforation);
  }

  updatePerforation(perforation: Perforation): Observable<boolean> {
    return this.http.put<boolean>(`${PeriforApiEndpoint}/thermalOperations/perforations/`, perforation);
  }

  deletePerforation(perforationId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${PeriforApiEndpoint}/thermalOperations/perforations/${perforationId}`);
  }

  getPericalOptions(): Observable<PericalOptions> {
    return this.http.get<PericalOptions>(`${PeriforApiEndpoint}/thermalOperations/pericalOptions`)
      .pipe(map(r=>  new PericalOptions({...r})));
  }

  setPericalOptions(pericalOptions: PericalOptions) {
    return this.http.put(`${PeriforApiEndpoint}/thermalOperations/pericalOptions`, pericalOptions);
  }

  getGasLiftValves(): Observable<GasLiftValve[]> {
    if (!this.gasLiftCache$) {
      this.gasLiftCache$ = this.http.get<GasLiftValve[]>(`${PeriforApiEndpoint}/thermalOperations/gasLiftValves`)
        .pipe(
          catchError(() => {
            return of([]);
          }),
          shareReplay(1)
        );
    }
    setTimeout(() => this.gasLiftCache$ = null, 1000);
    return this.gasLiftCache$;
  }

  setGasLiftValves(gasLiftValve: GasLiftValve): Observable<GasLiftValve> {
    return this.http.post<GasLiftValve>(`${PeriforApiEndpoint}/thermalOperations/gasLiftValves`, gasLiftValve);
  }

  updateGasliftValves(gasLiftValve: GasLiftValve): Observable<boolean> {
    return this.http.put<boolean>(`${PeriforApiEndpoint}/thermalOperations/gasLiftValves/`, gasLiftValve);
  }

    deleteGasLiftValve(glvId: string): Observable<boolean> {
    return this.http.delete<boolean>(`${PeriforApiEndpoint}/thermalOperations/gasLiftValves/${glvId}`);
  }
}
